import { Overbooking } from './../../product/product'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { ActivatedRoute, Router } from '@angular/router'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { NotificationService } from 'src/app/core/notification.service'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Course } from '../course'
import { Participation } from '../../booking/participation/participation'
import { courseSelectors } from '../course.selectors'
import { Booking } from '../../booking/booking'
import { bookingSelectors } from '../../booking/booking.selectors'
import { UntilDestroy } from '@ngneat/until-destroy'
import { bookingActions } from '../../booking/booking.actions'
import { courseActions } from '../course.actions'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { saleItemActions } from '../../sale/sale-item/sale-item.actions'
import { Raffle } from '../../raffle/raffle'
import { raffleActions } from '../../raffle/raffle.actions'
import { raffleSelectors } from '../../raffle/raffle.selectors'
import moment from 'moment-mini'
import { AddParticipationsModalComponent } from '../../booking/show-modal/add-participations-modal/add-participations-modal.component'
import { saleItemSelectors } from '../../sale/sale-item/sale-item.selectors'
import { SaleItem } from '../../sale/sale-item/sale-item'
import { DmsModalComponent } from 'src/app/shared/components/dms-modal/show.component'
import { CourseService } from '../course.service'
import { participationActions } from '../../booking/participation/participation.actions'
import { memberActions } from '../../member/member.actions'
import { roundTo } from 'src/app/shared/until'
import { memberSelectors } from '../../member/member.selectors'
import {
  ParticipantBookingOverviewRow,
  ParticipantBookingsModalComponent
} from './participant-bookings-modal.component'
@UntilDestroy()
@Component({
  selector: 'course-participation-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CourseParticipationTabComponent implements OnInit {
  public course$: Observable<Course>
  public courseId$: Observable<number>
  public isLoading$: Observable<boolean>
  public bookings$: Observable<Booking[]>
  public participations$: Observable<Participation[]>
  public selectedBookingCanOverbook: false
  public accessRights = AccessRights
  public overbookingTypes = Overbooking
  public BulkAction = BulkAction
  public linkedProductId: number
  public form: UntypedFormGroup
  public checkedIds: Map<number, boolean> = new Map()
  public didFetchCourse = false
  private alreadyFetchedSaleItemIds: number[] = []
  public isRaffle: boolean = false
  private raffles$: Observable<Raffle[]>
  private didFetchRaffle: boolean = false
  private alreadyFetchedMemberIds: number[] = []
  public onlyLastBookingParticipants: boolean = true
  private lastBookingToggled: BehaviorSubject<boolean> = new BehaviorSubject(
    true
  )

  constructor(
    private store: Store<AppState>,
    private modalService: BsModalService,
    private notificationService: NotificationService,
    private activeRoute: ActivatedRoute,
    private courseService: CourseService,
    private router: Router,
    private bsModalRef: BsModalRef
  ) {}

  ngOnInit() {
    this.isLoading$ = combineLatest([
      this.store.select(courseSelectors.selectIsLoading),
      this.store.select(bookingSelectors.selectIsLoading),
      this.store.select(participationSelectors.selectIsLoading)
    ]).pipe(map(([a, b, c]) => Boolean(a || b || c)))

    this.courseId$ = this.activeRoute.params.pipe(
      map((params) =>
        params.hasOwnProperty('courseId') ? Number(params['courseId']) : null
      ),
      filter((id) => Boolean(id)),
      distinctUntilChanged(),
      tap((courseId) => {
        if (!this.didFetchCourse) {
          this.didFetchCourse = true
          this.refreshCourseParticipations(courseId)
        }
        if (!this.didFetchRaffle) {
          this.didFetchRaffle = true
          this.store.dispatch(
            raffleActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  $and: [
                    { courseId },
                    {
                      closeDate: {
                        [CondOperator.GREATER_THAN_EQUALS]:
                          moment().format('YYYY-MM-DD')
                      }
                    },
                    {
                      drawDate: {
                        [CondOperator.GREATER_THAN_EQUALS]:
                          moment().format('YYYY-MM-DD')
                      }
                    }
                  ]
                },
                join: [{ field: 'raffleLots' }]
              })
            })
          )
        }
      })
    )

    this.course$ = this.courseId$.pipe(
      switchMap((courseId) =>
        this.store
          .select(courseSelectors.selectEntities)
          .pipe(map((entities) => entities[courseId]))
      )
    )

    this.bookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.course$
    ]).pipe(
      map(([bookings, course]) => {
        return bookings
          .filter((b) => b.courseId === course?.id)
          .sort((a, b) => (a?.startDate > b?.startDate ? -1 : 1))
      })
    )

    this.participations$ = combineLatest([
      this.bookings$,
      this.course$,
      this.store.select(participationSelectors.selectAll),
      this.lastBookingToggled
    ]).pipe(
      map(([bookings, _course, participations, lastBookingToggled]) => {
        const uniqueParticipants: Participation[] = []

        if (lastBookingToggled) {
          bookings = [...bookings]
            .sort(
              (a, b) =>
                moment(b.startDate).valueOf() - moment(a.startDate).valueOf()
            )
            .slice(0, 1)
        }

        for (const booking of bookings) {
          const bookingParticipations = participations.filter(
            (p) => p.bookingId === booking.id
          )

          for (const bookingParticipation of bookingParticipations) {
            const participantPersonId =
              bookingParticipation.invitedMemberId ||
              bookingParticipation.memberId

            if (
              participantPersonId &&
              !uniqueParticipants.find(
                (p) => (p.invitedMemberId || p.memberId) === participantPersonId
              )
            ) {
              uniqueParticipants.push(bookingParticipation)
            }
          }
        }

        return uniqueParticipants
      }),
      tap((uniqueParticipants) => {
        const memberIdsToFetch = uniqueParticipants
          .flatMap((p) => [p.memberId, p.invitedMemberId].filter(Boolean))
          .filter((id, index, self) => self.indexOf(id) === index)
          .filter((id) => !this.alreadyFetchedMemberIds.includes(id))

        if (memberIdsToFetch.length) {
          this.alreadyFetchedMemberIds.push(...memberIdsToFetch)

          this.store.dispatch(
            memberActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  id: { [CondOperator.IN]: memberIdsToFetch }
                }
              })
            })
          )
        }

        const saleItemIdsToFetch = uniqueParticipants
          .filter((p) => p.saleItemId)
          .map((p) => p.saleItemId)
          .filter((id, index, self) => self.indexOf(id) === index)
          .filter((id) => !this.alreadyFetchedSaleItemIds.includes(id))

        if (!saleItemIdsToFetch.length) {
          return
        }

        this.alreadyFetchedSaleItemIds.push(...saleItemIdsToFetch)
        this.store.dispatch(
          saleItemActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                id: { [CondOperator.IN]: saleItemIdsToFetch }
              },
              join: [{ field: 'sale' }, { field: 'sale.payments' }]
            })
          })
        )
      })
    )

    this.raffles$ = this.store.select(raffleSelectors.selectAll)
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    switch (bulkActionEvent.action) {
      case BulkAction.DELETE:
        const participationIds: number[] = bulkActionEvent.items

        if (participationIds.length > 1) {
          this.notificationService.emit('general.errorSelectionText', 'warning')
          return
        }

        combineLatest([
          this.store.select(participationSelectors.selectEntities),
          this.store.select(saleItemSelectors.selectEntities),
          this.store.select(bookingSelectors.selectAll)
        ])
          .pipe(take(1))
          .subscribe(([participationEntities, saleItemEntities, bookings]) => {
            const participation = participationEntities[participationIds[0]]
            const booking = bookings.find(
              (b) => b.id == participation.bookingId
            )
            const saleItem = saleItemEntities[participation.saleItemId]
            const participationPersonId =
              participation.invitedMemberId || participation.memberId
            const courseBookings = bookings.filter(
              (b) => b.courseId == booking.courseId
            )
            const courseBookingIds = courseBookings.map((b) => b.id)
            const futureBookings = courseBookings.filter((b) =>
              moment(b.startDate).isAfter(moment())
            )
            const futureBookingParticipants = Object.values(
              participationEntities
            ).filter(
              (p) =>
                (p.invitedMemberId || p.memberId) == participationPersonId &&
                futureBookings.find((b) => b.id == p.bookingId)
            )
            const courseBookingParticipants = Object.values(
              participationEntities
            ).filter(
              (p) =>
                (p.invitedMemberId || p.memberId) == participationPersonId &&
                courseBookingIds.includes(p.bookingId)
            )
            // get modal target refund amount from future booking participants amount to set as default in refund modal
            const futureBookingsAmount = futureBookingParticipants.reduce(
              (tot, p) => tot + p.amount,
              0
            )
            const defaultRefundAmount = this.getCourseRefundDefaultAmount(
              futureBookingsAmount,
              futureBookingParticipants,
              courseBookingParticipants,
              saleItemEntities
            )
            // paid course
            if (saleItem && saleItem.totalPrice && futureBookingsAmount) {
              console.log(
                `Got cancel of paid course for participant id ${participation.id} and future bookings amount of ${futureBookingsAmount}`
              )
              return this.startCourseRefundFlow(
                participationPersonId,
                booking.courseId,
                defaultRefundAmount > Number(saleItem.totalPrice)
                  ? Number(saleItem.totalPrice)
                  : defaultRefundAmount
              )
            } else {
              // free course
              console.log(
                `Got cancel of free course for participant id ${participation.id}`
              )

              this.courseService
                .memberCourseCancel(
                  participationPersonId,
                  booking.courseId,
                  null
                )
                .pipe(take(1))
                .subscribe((reponse) => {
                  if (reponse.success) {
                    this.notificationService.emit('alerts.success', 'success')
                    this.markCourseModalForRefresh()
                    this.refreshCourseParticipations(booking.courseId)
                  } else {
                    this.notificationService.emit(reponse.message, 'warning')
                  }
                })
            }
          })

        break
    }
  }

  public onClickCreateParticipation() {
    this.course$.pipe(take(1)).subscribe((course) => {
      this.store
        .select(bookingSelectors.selectByFilter, {
          field: 'courseId',
          value: course.id
        })
        .pipe(take(1), withLatestFrom(this.raffles$))
        .subscribe(([bookings, raffles]) => {
          const mostRestrictiveBooking: Booking = bookings.sort((a, b) => {
            return a.currentParticipantCount - b.currentParticipantCount
          })[0]

          const courseRaffle = raffles.find(
            (raffle) => raffle.courseId === course.id
          )

          this.modalService.show(AddParticipationsModalComponent, {
            initialState: {
              booking: mostRestrictiveBooking,
              bookings: bookings,
              course: course,
              raffle: courseRaffle
            },
            class: ''
          })
        })
    })
  }

  public onViewBookings(participation: Participation): void {
    combineLatest([
      this.bookings$,
      this.store.select(participationSelectors.selectAll),
      this.store.select(memberSelectors.selectEntities)
    ])
      .pipe(take(1))
      .subscribe(([bookings, participations, memberEntities]) => {
        const participantPersonId =
          participation.invitedMemberId || participation.memberId
        const courseBookings = bookings.sort((a, b) =>
          moment(a.startDate).isAfter(moment(b.startDate)) ? 1 : -1
        )

        const bookingRows: ParticipantBookingOverviewRow[] = courseBookings.map(
          (booking) => {
            const bookingParticipation = participations.find(
              (item) =>
                item.bookingId === booking.id &&
                (item.invitedMemberId || item.memberId) === participantPersonId
            )

            return {
              booking,
              participationStatus: bookingParticipation?.status,
              isAttending: Boolean(bookingParticipation)
            }
          }
        )

        const displayMember =
          memberEntities[
            participation.invitedMemberId || participation.memberId
          ]
        const participantName =
          `${displayMember?.firstName || participation.firstName || ''} ${
            displayMember?.lastName || participation.lastName || ''
          }`.trim()

        this.modalService.show(ParticipantBookingsModalComponent, {
          class: 'modal-lg modal-dialog-centered modal-dialog-scrollable',
          initialState: {
            participantName,
            bookingRows
          }
        })
      })
  }

  private startCourseRefundFlow(
    memberId: number,
    courseId: number,
    defaultRefundAmount: number
  ): void {
    // open modal with futureBookingsAmount as default target refund amount
    const modalRef = this.modalService.show(DmsModalComponent, {
      class: 'modal-md modal-dialog-centered modal-dialog-scrollable ',
      backdrop: 'static',
      initialState: {
        titleText: 'subscriptionModule.refund',
        detailText: 'general.doYouWantcontinue',
        showContainer: 'subscription', // this is weird for now we can improve this dms modal later
        refundAmount: defaultRefundAmount
      }
    })
    modalRef.onHide.pipe(take(1)).subscribe(() => {
      if (modalRef.content.action != 'yes') {
        return
      }

      // proceed with refund chosen by user now that user confirmed
      const targetRefundAmount =
        modalRef.content.selectedOption == 'noRefund'
          ? 0
          : modalRef.content.selectedOption == 'calculatedRefund'
            ? defaultRefundAmount
            : modalRef.content.selectedOption == 'customRefund' &&
                modalRef.content.customRefund >= 0
              ? modalRef.content.customRefund
              : 0

      console.log(
        `User closed refund modal with targetRefundAmount ${targetRefundAmount}`
      )

      this.courseService
        .memberCourseCancel(memberId, courseId, targetRefundAmount)
        .pipe(take(1))
        .subscribe((reponse) => {
          if (reponse.success) {
            this.notificationService.emit('alerts.success', 'success')
            this.markCourseModalForRefresh()

            this.modalService.hide()

            if (targetRefundAmount === 0) {
              this.refreshCourseParticipations(courseId)
              // done admin decided to not refund so no negative sale to be created
              return
            }
            // route admin to created credit sale
            this.router.navigate(['/sales', reponse.creditSaleId])
          } else {
            this.notificationService.emit(reponse.message, 'warning')
          }
        })
    })
  }

  private getCourseRefundDefaultAmount(
    futureBookingsAmount: number,
    futureBookingParticipants: Participation[],
    courseBookingParticipants: Participation[],
    saleItemEntities: Dictionary<SaleItem>
  ): number {
    const targetRefundCents = Math.round(100 * Number(futureBookingsAmount))

    if (
      !Number.isFinite(targetRefundCents) ||
      !futureBookingParticipants?.length ||
      !courseBookingParticipants?.length
    ) {
      return roundTo(futureBookingsAmount, 2)
    }

    const canceledParticipationsBySaleItemId = futureBookingParticipants.reduce(
      (participationsBySaleItemId, p) => {
        const saleItem = this.getParticipationSaleItem(p, saleItemEntities)

        if (
          !p.paid ||
          !p.saleItemId ||
          !saleItem?.courseId ||
          saleItem.totalPrice === null ||
          saleItem.totalPrice === undefined
        ) {
          return participationsBySaleItemId
        }

        participationsBySaleItemId.set(p.saleItemId, [
          ...(participationsBySaleItemId.get(p.saleItemId) ?? []),
          p
        ])

        return participationsBySaleItemId
      },
      new Map<number, Participation[]>()
    )

    let adjustedRefundCents = targetRefundCents

    for (const [
      saleItemId,
      canceledSaleItemParticipations
    ] of canceledParticipationsBySaleItemId) {
      const allSaleItemParticipations = courseBookingParticipants.filter(
        (p) => p.paid && p.saleItemId === saleItemId
      )

      if (
        allSaleItemParticipations.length !==
        canceledSaleItemParticipations.length
      ) {
        continue
      }

      const canceledAmountCents = canceledSaleItemParticipations.reduce(
        (sum, p) => sum + Math.round(100 * Number(p.amount)),
        0
      )
      const saleItem = this.getParticipationSaleItem(
        canceledSaleItemParticipations[0],
        saleItemEntities
      )
      const saleItemTotalCents = Math.round(100 * Number(saleItem?.totalPrice))

      if (!Number.isFinite(saleItemTotalCents)) {
        continue
      }

      adjustedRefundCents += saleItemTotalCents - canceledAmountCents
    }

    return roundTo(adjustedRefundCents / 100, 2)
  }

  private getParticipationSaleItem(
    participation: Participation,
    saleItemEntities: Dictionary<SaleItem>
  ): SaleItem {
    return (
      participation.saleItem ?? saleItemEntities?.[participation.saleItemId]
    )
  }

  private refreshCourseData(courseId: number): void {
    this.store.dispatch(
      courseActions.fetchOneById({
        id: courseId,
        params: RequestQueryBuilder.create({
          join: [{ field: 'product' }, { field: 'product.translations' }]
        })
      })
    )

    this.store.dispatch(
      bookingActions.fetchMany({
        params: this.getCourseBookingsQuery(courseId)
      })
    )
  }

  private markCourseModalForRefresh(): void {
    if (this.activeRoute.outlet === 'modal' && this.bsModalRef.content) {
      this.bsModalRef.content.shouldRefreshParent = true
    }
  }

  private refreshCourseParticipations(courseId: number): void {
    combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.store.select(participationSelectors.selectAll)
    ])
      .pipe(take(1))
      .subscribe(([bookings, participations]) => {
        const courseBookingIds = bookings
          .filter((booking) => booking.courseId === courseId)
          .map((booking) => booking.id)

        const staleParticipationIds = participations
          .filter((participation) =>
            courseBookingIds.includes(participation.bookingId)
          )
          .map((participation) => participation.id)

        if (staleParticipationIds.length) {
          this.store.dispatch(
            participationActions.removeMany({
              ids: staleParticipationIds
            })
          )
        }

        this.refreshCourseData(courseId)
      })
  }

  private getCourseBookingsQuery(courseId: number): RequestQueryBuilder {
    return RequestQueryBuilder.create({
      search: { courseId },
      join: [
        { field: 'product' },
        { field: 'product.translations' },
        { field: 'linkedProduct' },
        { field: 'linkedProduct.translations' },
        { field: 'supervisorBookings' },
        { field: 'participations' }
      ]
    })
  }
  public setOnlyLastBookingParticipants(value: boolean): void {
    this.onlyLastBookingParticipants = value
    this.lastBookingToggled.next(value)
  }
}
